import { CalendarCheck, CalendarX, MapPin, Percent, Plane, Users } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import { RichText } from '@/components/RichText';
import { getExperiencesByCollection } from '@/data/experiencesRepo';
import { useCollection } from '@/hooks/useCollections';
import { useExperience } from '@/hooks/useExperiences';
import {
  isNonRefundableBookingType,
  type ExperienceBookingType,
  type ExperienceType,
} from '@/types/types_experience';
import { parseHeroVideoSource } from '@/util/util_heroVideo';
import { GalleryCarousel } from './GalleryCarousel';
import { HeroVideo } from './HeroVideo';
import { InquiryFormCard } from './InquiryFormCard';
import { useScrollReveal } from './useScrollReveal';

// Guest-facing wording for the booking type (the admin labels are internal).
const bookingTypeNotes: Record<ExperienceBookingType, string> = {
  direct: 'Confirmed directly against live availability',
  inquiry: 'By inquiry — we confirm availability with you',
  'inquiry-immediate': 'By inquiry — confirmed or declined the same day, and firm once confirmed',
};

// The cancellation terms, stated with the rest of what to expect rather than as
// something to agree to at the point of asking.
//
// Read off the experience rather than fixed copy: the window is per experience
// (`freeCancellationDays`) and editable in the CMS, so stating a house rule here
// would quote terms the booking will not be held to. Deliberately a window and not
// a date — no date has been chosen yet at this point. The deadline itself is
// computed once a date is offered, by `computeFreeCancellationUntil`.
const cancellationLead = 'A private cultural experience held for you alone.';

function cancellationNoteFor(experience: ExperienceType): string {
  if (isNonRefundableBookingType(experience.bookingType)) {
    return `${cancellationLead} Confirmed bookings are firm and cannot be cancelled without charge.`;
  }
  // Zero days is the most generous policy there is, not the strictest — free
  // for the whole of the day before. Every window counts back from midnight
  // beginning the booked day; see `computeFreeCancellationUntil`.
  const days = experience.freeCancellationDays;
  if (days <= 0) {
    return `${cancellationLead} Free cancellation up to the day before the experience.`;
  }
  const window = `${days} ${days === 1 ? 'day' : 'days'}`;
  return `${cancellationLead} Free cancellation up to ${window} before; cancellations after that may incur a fee.`;
}

// Fixed brand copy — the same on every experience, not editable in the CMS.
const eightyDaysMessage =
  'Our Exclusive Experiences are curated for a limited number of select clients; these experiences are not ' +
  'available to the public. At Eighty Days, our core mission lies in supporting local artisans, talents, and the ' +
  "preservation of Japan's traditional culture, history, and art forms.";

/** Public experience detail page, rendered from the content model. */
export function PageExperienceDetail() {
  const { experienceId } = useParams<{ experienceId: string }>();
  const { experience, loading } = useExperience(experienceId);
  const { collection } = useCollection(experience?.collectionId);
  const [siblings, setSiblings] = useState<ExperienceType[]>([]);
  const revealRef = useScrollReveal<HTMLDivElement>(!loading && !!experience);

  useEffect(() => {
    const collectionId = experience?.collectionId;
    const load = collectionId ? getExperiencesByCollection(collectionId) : Promise.resolve([]);
    let cancelled = false;
    load.then((list) => {
      if (!cancelled) setSiblings(list.filter((e) => e.id !== experience?.id));
    });
    return () => {
      cancelled = true;
    };
  }, [experience]);

  if (loading) {
    return <div className='tw:px-6 tw:py-24 tw:text-center tw:text-ed-stone'>Loading…</div>;
  }

  if (!experience) {
    return (
      <div className='tw:px-6 tw:py-24 tw:text-center'>
        <h1 className='tw:font-serif tw:text-3xl'>Experience not found</h1>
        <Link to='/catalog' className='tw:mt-4 tw:inline-block tw:text-ed-gold tw:underline'>
          Back to the collection
        </Link>
      </div>
    );
  }

  // Either an uploaded file or a YouTube link; anything else stored here (a
  // Vimeo link, a typo) falls back to the hero image rather than showing a
  // broken player.
  const heroVideo = experience.layoutFormat === 'video-hero' ? parseHeroVideoSource(experience.videoUrl) : null;
  const guestRange =
    experience.minGuests == null
      ? null
      : `${experience.minGuests}${experience.maxGuests ? `–${experience.maxGuests}` : '+'} guests`;

  return (
    <article ref={revealRef}>
      {/* Hero */}
      <section className='tw:relative tw:flex tw:h-[70vh] tw:items-center tw:justify-center tw:overflow-hidden'>
        {heroVideo ? (
          // Keyed so switching to another experience starts its video from a
          // fresh poster rather than inheriting the previous one's faded-out
          // state: this route only changes its param, so nothing else remounts.
          <HeroVideo
            key={experience.videoUrl}
            source={heroVideo}
            posterImage={experience.heroImage}
            name={experience.name}
          />
        ) : (
          <img
            src={experience.heroImage}
            alt={experience.name}
            className='tw:absolute tw:inset-0 tw:h-full tw:w-full tw:object-cover'
          />
        )}
        <div className='tw:absolute tw:inset-0 tw:bg-gradient-to-b tw:from-ed-black/30 tw:via-ed-black/60 tw:to-ed-black/95' />

        <div className='tw:relative tw:z-10 tw:mx-auto tw:max-w-4xl tw:px-6 tw:text-center'>
          <span className='tw:mb-4 tw:inline-block tw:text-xs tw:tracking-[0.4em] tw:uppercase tw:text-ed-gold'>
            {collection && collection.name}
          </span>
          {/* Dropped entirely when no venue is named, rather than left as a pin
              pointing at nothing: the venue is optional now, and plenty of the
              catalogue is a walking tour rather than a building. */}
          {experience.venueName && (
            <div className='tw:mb-6 tw:flex tw:items-center tw:justify-center tw:gap-2'>
              <MapPin className='tw:size-4 tw:text-ed-gold' />
              <p className='tw:text-xs tw:tracking-[0.4em] tw:uppercase tw:text-ed-gold'>{experience.venueName}</p>
            </div>
          )}
          <h1 className='tw:mb-4 tw:font-serif tw:text-5xl tw:leading-tight tw:text-white tw:md:text-6xl tw:lg:text-7xl'>
            {experience.name}
          </h1>
          <p className='tw:mx-auto tw:max-w-2xl tw:text-base tw:text-white/60'>{experience.shortDescription}</p>
        </div>
      </section>

      {/* Experience details + inquiry */}
      <section className='tw:bg-ed-cream tw:px-6 tw:py-24'>
        <div className='tw:mx-auto tw:max-w-6xl'>
          <div className='tw:grid tw:grid-cols-1 tw:items-start tw:gap-16 tw:lg:grid-cols-2'>
            {/* Left: description */}
            <div className='ed-scroll-reveal'>
              <p className='tw:mb-6 tw:text-sm tw:tracking-[0.3em] tw:uppercase tw:text-ed-gold'>The Experience</p>
              <h2 className='tw:mb-8 tw:font-serif tw:text-4xl tw:leading-tight tw:text-ed-black tw:md:text-5xl'>
                {experience.subtitle || 'About this Experience'}
              </h2>

              <RichText
                html={experience.fullDescription}
                className='tw:space-y-6 tw:text-lg tw:leading-relaxed tw:text-ed-charcoal/80'
              />

              {experience.closingStatement && (
                <p className='tw:mt-6 tw:font-serif tw:text-xl tw:italic tw:leading-relaxed tw:text-ed-umber'>
                  {experience.closingStatement}
                </p>
              )}

              <div className='tw:mt-12 tw:space-y-6'>
                <h3 className='tw:font-serif tw:text-2xl tw:text-ed-black'>What to Expect</h3>

                <div className='tw:space-y-4'>
                  <ExpectCard
                    icon={<CalendarCheck className='tw:size-6 tw:text-ed-gold' />}
                    title='Booking'
                    value={bookingTypeNotes[experience.bookingType]}
                  />
                  {guestRange && (
                    <ExpectCard
                      icon={<Users className='tw:size-6 tw:text-ed-gold' />}
                      title='Group Size'
                      value={guestRange}
                    />
                  )}
                  <ExpectCard
                    icon={<MapPin className='tw:size-6 tw:text-ed-gold' />}
                    title='Location'
                    value={experience.venueName ? experience.venueName : 'To be determined' }
                  />
                  <ExpectCard
                    icon={<CalendarX className='tw:size-6 tw:text-ed-gold' />}
                    title='Cancellation'
                    value={cancellationNoteFor(experience)}
                  />
                  {experience.commissionEnabled && (
                    <ExpectCard
                      icon={<Percent className='tw:size-6 tw:text-ed-gold' />}
                      title='Partner Commission'
                      value={`${experience.commissionPercent}% on confirmed bookings`}
                    />
                  )}
                  {experience.famEnabled && (
                    <ExpectCard
                      icon={<Plane className='tw:size-6 tw:text-ed-gold' />}
                      title='FAM Trip'
                      value='Available for partner familiarisation visits'
                    />
                  )}
                </div>
              </div>

              {experience.includes.length > 0 && (
                <div className='tw:mt-12'>
                  <h3 className='tw:mb-4 tw:font-serif tw:text-2xl tw:text-ed-black'>Includes</h3>
                  <ul className='tw:space-y-3 tw:text-ed-charcoal/80'>
                    {experience.includes.map((item, i) => (
                      <li key={i} className='tw:flex tw:items-center tw:gap-3'>
                        <span className='tw:size-2 tw:shrink-0 tw:rounded-full tw:bg-ed-gold' />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <GalleryCarousel images={experience.galleryImages} name={experience.name} />
            </div>

            {/* Right: inquiry form, then the map */}
            <div className='ed-scroll-reveal' style={{ transitionDelay: '200ms' }}>
              {/* Keyed so moving to another experience starts a fresh request
                  rather than carrying the previous guest's details across: this
                  route only changes its param, so the form would otherwise stay
                  mounted with everything typed into it. */}
              <InquiryFormCard key={experience.id} experience={experience} />

              {experience.googleMapsEmbedUrl && (
                <div className='tw:mt-8 tw:aspect-video tw:w-full tw:overflow-hidden'>
                  <iframe
                    src={experience.googleMapsEmbedUrl}
                    title={`Map for ${experience.name}`}
                    className='tw:h-full tw:w-full tw:border-0'
                    loading='lazy'
                    referrerPolicy='no-referrer-when-downgrade'
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Related experiences from the same collection */}
      {siblings.length > 0 && (
        <section className='tw:border-t tw:border-ed-umber/10 tw:bg-white tw:px-6 tw:py-24'>
          <div className='tw:mx-auto tw:max-w-6xl'>
            <div className='tw:mb-16 tw:text-center'>
              <p className='tw:mb-4 tw:text-sm tw:tracking-[0.3em] tw:uppercase tw:text-ed-gold'>The Collection</p>
              <h2 className='tw:font-serif tw:text-4xl tw:text-ed-black'>
                {collection ? `More from ${collection.name}` : 'More Experiences'}
              </h2>
            </div>

            <div className='tw:grid tw:grid-cols-1 tw:gap-8 tw:md:grid-cols-3'>
              {siblings.map((sibling) => (
                <RelatedCard key={sibling.id} experience={sibling} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Message from Eighty Days */}
      <section className='tw:border-t tw:border-ed-umber/10 tw:bg-ed-cream tw:px-6 tw:py-20'>
        <div className='tw:mx-auto tw:max-w-3xl tw:text-center'>
          <img
            src='/eightydays_balloon.png'
            alt='Eighty Days'
            className='tw:mx-auto tw:mb-8 tw:h-16 tw:w-auto tw:opacity-80'
          />

          <h2 className='tw:mb-6 tw:font-serif tw:text-2xl tw:tracking-wide tw:text-ed-black tw:md:text-3xl'>
            A Message from Eighty Days
          </h2>

          <p className='tw:mx-auto tw:max-w-2xl tw:text-base tw:font-light tw:leading-relaxed tw:text-ed-charcoal/70 tw:md:text-lg'>
            {eightyDaysMessage}
          </p>

          <div className='tw:mt-10 tw:flex tw:items-center tw:justify-center'>
            <div className='tw:h-px tw:w-16 tw:bg-ed-gold/50' />
            <span className='tw:mx-4 tw:text-xs tw:tracking-[0.3em] tw:uppercase tw:text-ed-stone'>
              Exclusive Experiences
            </span>
            <div className='tw:h-px tw:w-16 tw:bg-ed-gold/50' />
          </div>
        </div>
      </section>
    </article>
  );
}

/** One of the icon rows in the "What to Expect" stack. */
function ExpectCard({ icon, title, value }: { icon: React.ReactNode; title: string; value: string }) {
  return (
    <div className='tw:flex tw:items-start tw:gap-4 tw:bg-white tw:p-4'>
      <div className='tw:flex tw:size-12 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-full tw:bg-ed-gold/10'>
        {icon}
      </div>
      <div>
        <h4 className='tw:mb-1 tw:font-medium tw:text-ed-black'>{title}</h4>
        <p className='tw:text-sm tw:text-ed-stone'>{value}</p>
      </div>
    </div>
  );
}

/** The simpler card style the mock uses for the related-experiences row. */
function RelatedCard({ experience }: { experience: ExperienceType }) {
  return (
    <Link to={`/experiences/${experience.id}`} className='tw:group tw:block'>
      <div className='tw:overflow-hidden tw:bg-ed-cream'>
        <div className='tw:h-48 tw:overflow-hidden'>
          <img
            src={experience.heroImage}
            alt={experience.name}
            loading='lazy'
            className='tw:h-full tw:w-full tw:object-cover tw:transition-transform tw:duration-500 tw:group-hover:scale-105'
          />
        </div>
        <div className='tw:p-6'>
          <h3 className='tw:mb-2 tw:font-serif tw:text-xl tw:text-ed-black tw:transition-colors tw:group-hover:text-ed-gold'>
            {experience.name}
          </h3>
          <p className='tw:text-sm tw:text-ed-stone'>{experience.shortDescription}</p>
        </div>
      </div>
    </Link>
  );
}
